import { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  TrendingUp as TrendingUpIcon,
  Users as PeopleIcon,
  DollarSign as AttachMoneyIcon,
  Globe as PublicIcon,
  Clock as AccessTimeIcon,
  RefreshCw as RefreshIcon,
  Monitor as DevicesIcon,
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import Map from "../maps/Map";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";
const COLORS = ["#007AFF", "#34C759", "#FF9500", "#FF3B30", "#5856D6"];

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const CHART_GRID = "rgba(255,255,255,0.07)";
const CHART_AXIS = "rgba(255,255,255,0.35)";
const CHART_TOOLTIP = {
  background: "rgba(17,17,19,0.97)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "10px",
  color: "#fff",
};

const mockData = {
  topRoutes: [
    { route: "/api/expenses", calls: 45678, avgResponseTime: "124ms" },
    { route: "/api/users", calls: 34567, avgResponseTime: "98ms" },
    { route: "/api/analytics", calls: 23456, avgResponseTime: "156ms" },
    { route: "/api/subscriptions", calls: 15678, avgResponseTime: "112ms" },
  ],
};

interface AnalyticsData {
  overview: {
    totalUsers: number;
    activeUsers: number;
    totalExpenses: number;
    recentSignups: number;
    mrr: number;
    tierBreakdown: { free: number; standard: number; premium: number };
  };
  userGrowth: Array<{ date: string; new_users: number; total_users: number }>;
  geoData: {
    usaStates: Array<{ state: string; abbr: string; users: number }>;
    worldCountries: Array<{ country: string; flag: string; users: number }>;
  };
  pageViews: { topPages: Array<{ page: string; views: number; uniqueVisitors: number; avgTime: string }> };
  mostActiveUsers: { topUsers: Array<{ name: string; email: string; activityCount: number; tier: string }> };
  devices: Array<{ device: string; users: number; percentage: number }>;
  trafficSources: Array<{ source: string; sessions: number; percentage: number }>;
}

const EMPTY_DATA: AnalyticsData = {
  overview: { totalUsers: 0, activeUsers: 0, totalExpenses: 0, recentSignups: 0, mrr: 0, tierBreakdown: { free: 0, standard: 0, premium: 0 } },
  userGrowth: [],
  geoData: { usaStates: [], worldCountries: [] },
  pageViews: { topPages: [] },
  mostActiveUsers: { topUsers: [] },
  devices: [],
  trafficSources: [],
};

function getTierBadge(tier: string) {
  switch (tier) {
    case 'premium': return 'bg-purple/25 text-purple border border-purple/40';
    case 'standard': return 'bg-primary/25 text-primary border border-primary/40';
    default: return 'bg-white/[0.08] text-white/70 border border-white/20';
  }
}

interface PostingStatus {
  enabled: boolean;
  nextRunIn: number | null;
  nextScheduledPost: string | null;
  stats: { pendingPosts: number; failedPosts: number; successfulToday: number };
}

function AnalyticsDashboard() {
  const [timeRange, setTimeRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [_focusedMeta, setFocusedMeta] = useState<unknown>(null);
  const [postingStatus, setPostingStatus] = useState<PostingStatus | null>(null);

  useEffect(() => { fetchAnalyticsData(); }, [timeRange]);
  useEffect(() => { fetchPostingStatus(); }, []);

  const fetchPostingStatus = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API_URL}/api/admin/jobs/automation-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setPostingStatus(json.automation);
      }
    } catch {
      // non-critical, ignore
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const days = timeRange === "24h" ? 1 : timeRange === "7d" ? 7 : 30;

      const [overviewRes, userGrowthRes, geoDataRes, pageViewsRes, mostActiveUsersRes, devicesRes, trafficSourcesRes] =
        await Promise.all([
          fetch(`${API_URL}/api/admin/dashboard/overview`, { headers }),
          fetch(`${API_URL}/api/admin/analytics/user-growth?days=${days}`, { headers }),
          fetch(`${API_URL}/api/admin/analytics/geographic-data`, { headers }),
          fetch(`${API_URL}/api/admin/analytics/page-views`, { headers }),
          fetch(`${API_URL}/api/admin/analytics/most-active-users`, { headers }),
          fetch(`${API_URL}/api/admin/analytics/devices`, { headers }),
          fetch(`${API_URL}/api/admin/analytics/traffic-sources`, { headers }),
        ]);

      if (!overviewRes.ok) throw new Error(`Overview failed: ${overviewRes.status}`);

      const [overview, userGrowth, geoData, pageViews, mostActiveUsers, devices, trafficSources] =
        await Promise.all([
          overviewRes.json(), userGrowthRes.json(), geoDataRes.json(),
          pageViewsRes.json(), mostActiveUsersRes.json(), devicesRes.json(), trafficSourcesRes.json(),
        ]);

      setData({
        overview: overview || EMPTY_DATA.overview,
        userGrowth: Array.isArray(userGrowth) ? userGrowth : [],
        geoData: {
          usaStates: Array.isArray(geoData?.usaStates) ? geoData.usaStates : [],
          worldCountries: Array.isArray(geoData?.worldCountries) ? geoData.worldCountries : [],
        },
        pageViews: { topPages: Array.isArray(pageViews?.topPages) ? pageViews.topPages : [] },
        mostActiveUsers: { topUsers: Array.isArray(mostActiveUsers?.topUsers) ? mostActiveUsers.topUsers : [] },
        devices: Array.isArray(devices?.devices) ? devices.devices : [],
        trafficSources: Array.isArray(trafficSources?.sources) ? trafficSources.sources : [],
      });
      setError(null);
    } catch (err: unknown) {
      console.error("Analytics fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load analytics data");
      setData(EMPTY_DATA);
    } finally {
      setLoading(false);
    }
  };

  const currentTheme = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark') ? 'dark' : 'light';

  const usaStatesForMap = useMemo(() => {
    if (!data?.geoData?.usaStates) return [];
    return data.geoData.usaStates.map((s: Record<string, unknown>) => ({
      state: s.state || s.name || s.stateName,
      abbr: s.abbr || s.stateAbbr || s.code,
      users: Number(s.users || 0),
      cities: Array.isArray(s.cities) ? s.cities.map((c: Record<string, unknown>) => ({
        name: c.name, users: Number(c.users || 0),
        lat: c.lat ?? c.latitude ?? null, lon: c.lon ?? c.longitude ?? null,
      })) : (s.citiesList || []),
    }));
  }, [data]);

  const worldCountriesForMap = useMemo(() => {
    if (!data?.geoData?.worldCountries) return [];
    return data.geoData.worldCountries.map((c: Record<string, unknown>) => ({
      country: c.country || c.name, code: c.code || c.iso2 || c.iso3,
      users: Number(c.users || 0), lat: c.lat ?? c.latitude ?? null,
      lon: c.lon ?? c.longitude ?? null, flag: c.flag ?? c.emoji,
    }));
  }, [data]);

  const dateRangeLabel = useMemo(() => {
    const today = new Date();
    if (timeRange === "24h") return "Last 24 Hours";
    const days = timeRange === "7d" ? 7 : 30;
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - days + 1);
    return `${pastDate.toLocaleDateString("en-US", { month: 'short', day: 'numeric' })} - ${today.toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [timeRange]);

  const geoTableData = useMemo(() => {
    const US_TOTAL = usaStatesForMap.reduce((s, row) => s + row.users, 0);
    const usRow = { country: "United States", users: US_TOTAL, percentageChange: "+63.6%", flag: "🇺🇸" };
    const others = worldCountriesForMap
      .filter(c => c.users > 0 && String(c.country).toLowerCase() !== "united states")
      .map(c => ({ ...c, percentageChange: c.users > 10 ? "+8.3%" : c.users === 0 ? "-100%" : null }));
    return [usRow, ...others, { country: "Ecuador", users: 0, percentageChange: "-100%", flag: "🇪🇨" }]
      .sort((a, b) => b.users - a.users);
  }, [usaStatesForMap, worldCountriesForMap]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div className="w-8 h-8 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
        <p className="text-white/40 text-sm">Loading analytics…</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <p className="text-red text-sm">{error}</p>
        <button onClick={fetchAnalyticsData}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-colors">
          <RefreshIcon size={14} /> Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const statCards = [
    { title: "Total Users", value: (data.overview.totalUsers ?? 0).toLocaleString(), icon: <PeopleIcon size={18} />, color: "#007AFF", trend: "+12.5%" },
    { title: "Active Users (30d)", value: (data.overview.activeUsers ?? 0).toLocaleString(), icon: <TrendingUpIcon size={18} />, color: "#34C759", trend: "+8.3%" },
    { title: "Total Expenses", value: (data.overview.totalExpenses ?? 0).toLocaleString(), icon: <PublicIcon size={18} />, color: "#FF9500", trend: "+15.2%" },
    { title: "New Signups (7d)", value: (data.overview.recentSignups ?? 0).toLocaleString(), icon: <AccessTimeIcon size={18} />, color: "#9b59b6", trend: "+2.4%" },
    { title: "Monthly Revenue", value: `$${(data.overview.mrr ?? 0).toFixed(2)}`, icon: <AttachMoneyIcon size={18} />, color: "#16a085", trend: "+18.7%" },
    { title: "Free Users", value: (data.overview.tierBreakdown?.free ?? 0).toLocaleString(), icon: <PeopleIcon size={18} />, color: "#95a5a6", trend: `${data.overview.totalUsers > 0 ? (((data.overview.tierBreakdown?.free ?? 0) / data.overview.totalUsers) * 100).toFixed(1) : 0}%` },
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics Overview</h1>
          <p className="text-white/40 text-sm mt-0.5">Real-time insights and performance metrics</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Time range filter */}
          <div className="flex bg-white/[0.05] border border-white/[0.08] rounded-xl p-1 gap-1">
            {[{ id: "24h", label: "24 Hours" }, { id: "7d", label: "7 Days" }, { id: "30d", label: "30 Days" }].map(r => (
              <button key={r.id} onClick={() => setTimeRange(r.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${timeRange === r.id ? 'bg-primary text-white' : 'text-white/50 hover:text-white/80'}`}>
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={fetchAnalyticsData}
            className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3 py-2 text-sm font-medium transition-colors">
            <RefreshIcon size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red/10 border border-red/20 text-red rounded-xl px-4 py-3 text-sm">{error}</div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((card, i) => (
          <div key={i} className={`${CARD} p-4`} style={{ borderTopColor: card.color, borderTopWidth: 2 }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: `${card.color}20`, color: card.color }}>
              {card.icon}
            </div>
            <p className="text-white/40 text-xs mb-0.5">{card.title}</p>
            <p className="text-xl font-bold text-white leading-tight">{card.value}</p>
            <span className="text-xs font-semibold text-green">{card.trend}</span>
          </div>
        ))}
      </div>

      {/* Auto-Posting Status */}
      {postingStatus && (
        <div className={`${CARD} p-4 flex flex-wrap items-center gap-4`}>
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-green" />
              <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-green animate-ping opacity-60" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm leading-tight">Auto-Posting Active</p>
              <p className="text-white/40 text-xs mt-0.5">
                {postingStatus.nextRunIn !== null
                  ? `Next run in ${postingStatus.nextRunIn} min`
                  : "No posts scheduled"}
                {postingStatus.nextScheduledPost && ` · Next post: ${new Date(postingStatus.nextScheduledPost).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <Send size={13} className="text-primary" />
              <span className="text-white font-semibold">{postingStatus.stats.successfulToday}</span>
              <span className="text-white/40 text-xs">today</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Loader2 size={13} className="text-orange/80" />
              <span className="text-white font-semibold">{postingStatus.stats.pendingPosts}</span>
              <span className="text-white/40 text-xs">pending</span>
            </div>
            {postingStatus.stats.failedPosts > 0 && (
              <div className="flex items-center gap-1.5">
                <XCircle size={13} className="text-red" />
                <span className="text-red font-semibold">{postingStatus.stats.failedPosts}</span>
                <span className="text-white/40 text-xs">failed</span>
              </div>
            )}
            {postingStatus.stats.failedPosts === 0 && (
              <div className="flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-green" />
                <span className="text-white/40 text-xs">No failures</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Growth Chart */}
      <div className={`${CARD} p-5`}>
        <h2 className="text-sm font-semibold text-white mb-1">User Growth Over Time</h2>
        <p className="text-white/40 text-xs mb-4">New registrations and cumulative total</p>
        {data.userGrowth.length > 0 ? (
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.userGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                <XAxis dataKey="date" stroke={CHART_AXIS} tick={{ fontSize: 11 }} />
                <YAxis stroke={CHART_AXIS} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={CHART_TOOLTIP} />
                <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                <Line type="monotone" dataKey="new_users" stroke="#007AFF" strokeWidth={2} name="New Users" dot={false} />
                <Line type="monotone" dataKey="total_users" stroke="#34C759" strokeWidth={2} name="Total Users" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 text-white/30 text-sm">📈 No user growth data available yet</div>
        )}
      </div>

      {/* Geo map + Country table */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
        <Map
          worldCountries={geoTableData}
          height={420}
          theme={currentTheme as "light" | "dark"}
          dateRange={dateRangeLabel}
          onFeatureClick={setFocusedMeta}
        />

        <div className={`${CARD} p-4`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Active users <span className="text-white/40 font-normal text-xs">by Country</span></h3>
            <span className="text-green text-sm">✓</span>
          </div>
          <div className="grid grid-cols-2 pb-2 mb-1 border-b border-white/[0.06]">
            <span className="text-[10px] font-semibold text-white/30 uppercase tracking-wider">Country</span>
            <span className="text-[10px] font-semibold text-white/30 uppercase tracking-wider text-right">Active Users</span>
          </div>
          <div className="space-y-0 max-h-[340px] overflow-y-auto">
            {geoTableData.slice(0, 7).map((item: any, i) => {
              const maxUsers = geoTableData[0]?.users || 1;
              const barPct = (item.users / maxUsers) * 100;
              const trendColor = item.percentageChange?.startsWith('+') ? '#34C759' : item.percentageChange?.startsWith('-') ? '#FF3B30' : 'rgba(255,255,255,0.3)';
              return (
                <div key={i} className="relative py-2.5 border-b border-white/[0.04] last:border-0">
                  <div className="absolute inset-0 rounded" style={{ width: `${barPct}%`, background: 'rgba(0,122,255,0.08)' }} />
                  <div className="relative grid grid-cols-2">
                    <div className="flex items-center gap-2">
                      <span>{item.flag || '🌐'}</span>
                      <span className="text-white/80 text-xs font-medium">{item.country}</span>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-white font-semibold text-xs">{item.users.toLocaleString()}</span>
                      {item.percentageChange && <span className="text-[10px] font-medium" style={{ color: trendColor }}>{item.percentageChange}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-white/[0.06] text-right">
            <span className="text-primary text-xs font-medium cursor-pointer hover:text-primary/80">View all countries →</span>
          </div>
        </div>
      </div>

      {/* Most Active Users */}
      <div className={CARD}>
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-semibold text-white">Most Active Users</h3>
        </div>
        {data.mostActiveUsers.topUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['User', 'Email', 'Activity Count', 'Tier'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.mostActiveUsers.topUsers.map((user, i) => (
                  <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-white/80 text-xs font-semibold">{user.name || 'Anonymous'}</td>
                    <td className="px-4 py-3 text-white/50 text-xs">{user.email ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-primary font-bold text-xs">{user.activityCount ?? 0}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getTierBadge(user.tier || 'free')}`}>
                        {(user.tier || 'free').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 text-white/30 text-sm">No active users data available yet</div>
        )}
      </div>

      {/* Device Breakdown + Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`${CARD} p-5`}>
          <h2 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
            <DevicesIcon size={15} className="text-primary" /> Device Breakdown
          </h2>
          {data.devices.length > 0 ? (
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.devices}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                  <XAxis dataKey="device" stroke={CHART_AXIS} tick={{ fontSize: 11 }} />
                  <YAxis stroke={CHART_AXIS} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={CHART_TOOLTIP} />
                  <Bar dataKey="users" fill="#007AFF" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-white/30 text-sm">No device data available yet</div>
          )}
        </div>

        <div className={`${CARD} p-5`}>
          <h2 className="text-sm font-semibold text-white mb-4">Traffic Sources</h2>
          {data.trafficSources.length > 0 ? (
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.trafficSources} dataKey="sessions" nameKey="source" cx="50%" cy="50%" outerRadius={90}
                    label={(e) => `${e.name ?? 'Unknown'}: ${((e.percent ?? 0) * 100).toFixed(1)}%`}
                    labelLine={{ stroke: 'rgba(255,255,255,0.3)' }}>
                    {data.trafficSources.map((_, i) => (
                      <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={CHART_TOOLTIP} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-white/30 text-sm">No traffic data available yet</div>
          )}
        </div>
      </div>

      {/* Most Visited Pages */}
      <div className={CARD}>
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-semibold text-white">Most Visited Pages</h3>
        </div>
        {data.pageViews.topPages.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Page', 'Views', 'Unique Visitors', 'Avg. Time'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.pageViews.topPages.map((page, i) => (
                  <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <code className="text-white/70 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">{page.page ?? '/unknown'}</code>
                    </td>
                    <td className="px-4 py-3 text-primary font-bold text-xs">{(page.views ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{(page.uniqueVisitors ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{page.avgTime ?? '0s'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 text-white/30 text-sm">No page view data available yet</div>
        )}
      </div>

      {/* Top API Routes */}
      <div className={CARD}>
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-semibold text-white">Top API Routes</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {['Route', 'API Calls', 'Avg. Response Time'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mockData.topRoutes.map((route, i) => (
                <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <code className="text-white/70 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded font-mono">{route.route}</code>
                  </td>
                  <td className="px-4 py-3 text-primary font-bold text-xs">{route.calls.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green/15 text-green">{route.avgResponseTime}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
